import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaCheck, FaTimes, FaEdit, FaTrashAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { CONTACT_DEPARTMENTS, getDepartmentLabel, getDepartmentMailbox } from '../../../config/contactDepartments';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;

  th, td {
    padding: 16px 12px;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const StatusButton = styled.button`
  background: ${props => props.ativo ? '#22c55e' : '#c62828'};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 4px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;

const ActionCell = styled.td`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #c0843d;
  font-size: 1.1rem;
  transition: background 0.2s;
  &:hover {
    background: #f8f8f8;
    color: #c62828;
  }
`;

const Contato = () => {
  const [contatos, setContatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagina, setPagina] = useState(1);
  const porPagina = 7;

  const fetchContatos = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${import.meta.env.VITE_URL_API}/contatos`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      setContatos(Array.isArray(data) ? data : []);
    } catch {
      setError('Erro ao buscar contatos.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchContatos();
  }, []);

  const getDepartamento = (item) => item.departamentoCONTATO || item.departamento || '';

  const handleEdit = (item) => {
    const departamento = getDepartamento(item);
    const mailbox = getDepartmentMailbox(departamento);
    Swal.fire({
      title: 'Visualizar/Editar contato',
      html: `<div style='text-align:left'>
        <b>Nome:</b> ${item.nomeCONTATO}<br/>
        <b>E-mail:</b> ${item.emailCONTATO}<br/>
        <b>Departamento:</b> ${departamento ? getDepartmentLabel(departamento) : '-'}${mailbox ? ` (${mailbox})` : ''}<br/>
        <b>Assunto:</b> ${item.assuntoCONTATO}<br/>
        <b>Mensagem:</b> <pre style='white-space:pre-wrap;text-align:left;font-family:inherit;background:#f8f8f8;padding:8px;border-radius:6px;'>${item.mensagemCONTATO}</pre>
        <b>Data:</b> ${new Date(item.dataENVIO).toLocaleString('pt-BR')}<br/>
        <b>Status:</b> ${item.statusCONTATO === 1 ? 'Ativo' : 'Inativo'}
        <hr style='margin: 16px 0; border: none; border-top: 1px solid #eee;'/>
        <b style='display:block;margin-bottom:8px'>Sua Resposta:</b>
        <textarea id='resposta-contato' rows='4' style='width:100%;padding:8px;border:1px solid #ddd;border-radius:6px;font-family:inherit;resize:vertical' placeholder='Digite sua resposta...'></textarea>
      </div>`,
      showCancelButton: true,
      confirmButtonText: 'Enviar Resposta',
      cancelButtonText: 'Cancelar',
      showDenyButton: true,
      denyButtonText: item.statusCONTATO === 1 ? 'Marcar como não lido' : 'Marcar como lido',
      preConfirm: async () => {
        const resposta = document.getElementById('resposta-contato').value;
        if (!resposta.trim()) {
          Swal.showValidationMessage('Por favor, digite uma resposta');
          return false;
        }
        
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        
        try {
          // Enviar resposta por email via API
          const response = await fetch(`${import.meta.env.VITE_URL_API}/contatos/${item.codigoCONTATO}/responder`, {
            method: 'POST',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              resposta: resposta.trim(),
              emailDestinatario: item.emailCONTATO,
              nomeDestinatario: item.nomeCONTATO,
              assuntoOriginal: item.assuntoCONTATO
            })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Erro ao enviar resposta');
          }
          
          Swal.fire({
            icon: 'success',
            title: 'Resposta Enviada!',
            text: `Resposta enviada com sucesso para ${item.emailCONTATO}`,
            confirmButtonText: 'OK'
          });
          
          // Marcar como lido automaticamente após responder
          await fetch(`${import.meta.env.VITE_URL_API}/contatos/${item.codigoCONTATO}`, {
            method: 'PATCH',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...item,
              statusCONTATO: 1
            })
          });
          setTimeout(fetchContatos, 400);
          
        } catch (error) {
          Swal.fire({
            icon: 'error',
            title: 'Erro ao Enviar',
            text: error?.message || 'Não foi possível enviar a resposta. Tente novamente.',
            confirmButtonText: 'OK'
          });
        }
      },
      preDeny: async () => {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        try {
          await fetch(`${import.meta.env.VITE_URL_API}/contatos/${item.codigoCONTATO}`, {
            method: 'PATCH',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...item,
              statusCONTATO: item.statusCONTATO === 1 ? 0 : 1
            })
          });
          setTimeout(fetchContatos, 400);
          Swal.fire('Sucesso', 'Status atualizado!', 'success');
        } catch {
          Swal.fire('Erro', 'Não foi possível atualizar o status.', 'error');
        }
      }
    });
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: 'Tem certeza?',
      text: `Deseja realmente excluir o contato de "${item.nomeCONTATO}"? Esta ação não poderá ser desfeita!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then(async (result) => {
      if (result.isConfirmed) {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        try {
          await fetch(`${import.meta.env.VITE_URL_API}/contatos/${item.codigoCONTATO}`, {
            method: 'DELETE',
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          setTimeout(fetchContatos, 400);
          Swal.fire('Sucesso', 'Contato excluído com sucesso!', 'success');
        } catch {
          Swal.fire('Erro', 'Não foi possível excluir o contato.', 'error');
        }
      }
    });
  };

  // Busca por nome ou e-mail + filtro de departamento
  const [search, setSearch] = useState('');
  const [departamentoFiltro, setDepartamentoFiltro] = useState('');
  const filteredContatos = contatos.filter(item =>
    ((item.emailCONTATO || '').toLowerCase().includes(search.toLowerCase()) ||
    (item.nomeCONTATO || '').toLowerCase().includes(search.toLowerCase())) &&
    (!departamentoFiltro || getDepartamento(item) === departamentoFiltro)
  ).sort((a, b) => new Date(b.dataENVIO) - new Date(a.dataENVIO));
  const paginadosFiltrados = filteredContatos.slice((pagina - 1) * porPagina, pagina * porPagina);

  return (
    <PageContainer style={{ background: '#f5f5f5' }}>
      <HeaderRow>
        <h1 style={{margin:0}}>Contatos</h1>
      </HeaderRow>
      <div style={{marginBottom:18,display:'flex',gap:12,alignItems:'center'}}>
        <input
          type="text"
          placeholder="Buscar por nome ou e-mail..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPagina(1); }}
          style={{padding:'12px',border:'1.2px solid #e0e0e0',borderRadius:8,fontSize:15,width:320,maxWidth:'100%'}}
        />
        <select
          value={departamentoFiltro}
          onChange={e => { setDepartamentoFiltro(e.target.value); setPagina(1); }}
          style={{padding:'12px',border:'1.2px solid #e0e0e0',borderRadius:8,fontSize:15}}
        >
          <option value="">Todos os departamentos</option>
          {CONTACT_DEPARTMENTS.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
      </div>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>E-mail</th>
              <th>Departamento</th>
              <th>Assunto</th>
              <th>Mensagem</th>
              <th>Data</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{textAlign:'center',padding:32}}><span>Carregando...</span></td></tr>
            ) : error ? (
              <tr><td colSpan={9} style={{textAlign:'center',padding:32,color:'#d32f2f'}}>{error}</td></tr>
            ) : paginadosFiltrados.length === 0 ? (
              <tr><td colSpan={9} style={{textAlign:'center',padding:32}}>Nenhum contato encontrado.</td></tr>
            ) : paginadosFiltrados.map(item => (
              <tr key={item.codigoCONTATO}>
                <td>{item.codigoCONTATO}</td>
                <td>{item.nomeCONTATO}</td>
                <td>{item.emailCONTATO}</td>
                <td>{getDepartamento(item) ? getDepartmentLabel(getDepartamento(item)) : '-'}</td>
                <td>{item.assuntoCONTATO}</td>
                <td><span title={item.mensagemCONTATO}>{item.mensagemCONTATO.length > 40 ? item.mensagemCONTATO.slice(0, 40) + '...' : item.mensagemCONTATO}</span></td>
                <td>{new Date(item.dataENVIO).toLocaleString('pt-BR')}</td>
                <td>
                  <span style={{
                    display:'inline-block',
                    padding:'3px 12px',
                    borderRadius:8,
                    fontWeight:600,
                    fontSize:13,
                    color: item.statusCONTATO === 1 ? '#22c55e' : '#c62828',
                    background: item.statusCONTATO === 1 ? '#e6faed' : '#fdeaea',
                  }}>
                    {item.statusCONTATO === 1 ? 'Lido' : 'Não lido'}
                  </span>
                </td>
                <ActionCell>
                  <IconButton title="Visualizar/Editar" onClick={()=>handleEdit(item)}><FaEdit /></IconButton>
                  <IconButton title="Excluir" onClick={()=>handleDelete(item)}><FaTrashAlt /></IconButton>
                </ActionCell>
              </tr>
            ))}
          </tbody>
        </Table>
        {/* Paginação */}
        <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,margin:'24px 0'}}>
          <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={pagina===1} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===1?'not-allowed':'pointer'}}>Anterior</button>
          {[...Array(Math.ceil(filteredContatos.length/porPagina))].map((_,i)=>(
            <button
              key={i}
              onClick={()=>setPagina(i+1)}
              style={{padding:'6px 12px',borderRadius:6,border:'1.5px solid '+(pagina===i+1?'#34574A':'#eee'),background:pagina===i+1?'#34574A':'#fff',color:pagina===i+1?'#fff':'#222',fontWeight:600,cursor:'pointer'}}
            >{i+1}</button>
          ))}
          <button onClick={()=>setPagina(p=>Math.min(Math.ceil(filteredContatos.length/porPagina),p+1))} disabled={pagina===Math.ceil(filteredContatos.length/porPagina)} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #eee',background:'#fff',cursor:pagina===Math.ceil(filteredContatos.length/porPagina)?'not-allowed':'pointer'}}>Próxima</button>
        </div>
      </TableWrapper>
    </PageContainer>
  );
};

export default Contato;
